import React, { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import { fetchUserOrdersThunk } from "../store/slices/ordersSlice";

const STAGES = [
  { id: "pending", label: "Order Placed" },
  { id: "confirmed", label: "Confirmed" },
  { id: "processing", label: "Processing" },
  { id: "shipped", label: "In Transit" },
  { id: "delivered", label: "Delivered" },
] as const;

const OrderDetail: React.FC = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { orders, isLoading, error } = useSelector((state: RootState) => state.orders);
  const { currentTenant } = useSelector((state: RootState) => state.tenant);
  const user = useSelector((state: RootState) => state.auth.user);

  useEffect(() => {
    if (orders.length === 0 && user?.id && currentTenant?.id) {
      dispatch(
        fetchUserOrdersThunk({
          tenantId: currentTenant.id,
          userId: user.id,
        }) as any
      );
    }
  }, [dispatch, orders.length, user, currentTenant]);

  const order = useMemo(() => orders.find((item) => item.id === id), [orders, id]);
  const currentStageIndex = useMemo(() => {
    if (!order) return -1;
    return STAGES.findIndex((stage) => stage.id === order.status);
  }, [order]);

  const paymentClass = useMemo(() => {
    if (!order) return "bg-gray-100 text-gray-700";
    if (order.payment_status === "paid") return "bg-green-100 text-green-700";
    if (order.payment_status === "failed") return "bg-red-100 text-red-700";
    return "bg-yellow-100 text-yellow-700";
  }, [order]);

  return (
    <div className="max-w-4xl mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Order Tracking</h1>
        <Link to="/app/orders" className="text-green-700 hover:underline text-sm">
          Back to Orders
        </Link>
      </div>

      {isLoading && (
        <div className="text-center py-12 text-green-600 font-semibold">
          Loading order details...
        </div>
      )}

      {!isLoading && error && (
        <div className="text-center py-12 text-red-600 font-semibold">{error}</div>
      )}

      {!isLoading && !error && !order && (
        <div className="bg-white rounded-lg p-8 text-center shadow">
          <p className="text-gray-600">Order not found.</p>
        </div>
      )}

      {!isLoading && !error && order && (
        <>
          <div className="bg-white rounded-xl shadow border border-gray-100 p-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-xs text-gray-500">Order Number</p>
                <p className="font-mono text-sm text-gray-900">{order.order_number}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Placed {new Date(order.created_at).toLocaleString()}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500 mb-1">Total Amount</p>
                <p className="text-2xl font-bold text-green-700">
                  ₦{order.total_amount.toLocaleString()}
                </p>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span className="px-2 py-1 rounded bg-blue-100 text-blue-700 text-xs font-semibold">
                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
              </span>
              <span className={`px-2 py-1 rounded text-xs font-semibold ${paymentClass}`}>
                Payment:{" "}
                {order.payment_status.charAt(0).toUpperCase() +
                  order.payment_status.slice(1)}
              </span>
              {order.delivery_date && (
                <span className="px-2 py-1 rounded bg-gray-100 text-gray-700 text-xs font-semibold">
                  ETA: {new Date(order.delivery_date).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>

          {order.status !== "cancelled" && (
            <div className="bg-white rounded-xl shadow border border-gray-100 p-5">
              <h2 className="font-semibold text-gray-900 mb-4">Order Progress</h2>
              <div className="space-y-4">
                {STAGES.map((stage, index) => {
                  const done = index <= currentStageIndex;
                  const active = index === currentStageIndex;
                  return (
                    <div key={stage.id} className="flex items-center gap-3">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                          done
                            ? "bg-green-600 text-white"
                            : "bg-gray-200 text-gray-500"
                        } ${active ? "ring-4 ring-green-100" : ""}`}
                      >
                        {index + 1}
                      </div>
                      <p
                        className={`font-medium ${
                          done ? "text-gray-900" : "text-gray-500"
                        }`}
                      >
                        {stage.label}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl shadow border border-gray-100 p-5">
            <h2 className="font-semibold text-gray-900 mb-4">Items</h2>
            <div className="space-y-3">
              {order.items.map((item) => (
                <div
                  key={item.id}
                  className="border border-gray-200 rounded-lg p-3 flex items-center justify-between"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Product ID: {item.product_id}
                    </p>
                    <p className="text-xs text-gray-500">
                      Quantity: {item.quantity} x ₦{item.unit_price.toLocaleString()}
                    </p>
                  </div>
                  <p className="font-semibold text-gray-900">
                    ₦{item.total_price.toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
            {order.notes && (
              <div className="mt-4 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                <span className="font-semibold text-gray-700">Notes:</span> {order.notes}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default OrderDetail;
